/**
 * "Request documents" (2026-10-09, flag `document_requests`, default off): an employer asks an
 * applicant or a team member for specific credentials / documents, optionally by a due date; the
 * candidate uploads them from /documents/requests and links each file to the item it answers.
 *
 * Data lives in the server-only collection documentRequests/{id}, written and read ONLY through the
 * documentRequestsApi callable (functions/src/documentRequests.ts). The stored item status is only
 * 'requested' or 'uploaded'; "Documents Reviewed" and "Overdue" are computed on every read from the
 * candidate's live profile (the trusted certificationChecks) and the calendar, so they never go stale.
 *
 * This file is the pure logic both sides share (synced into functions/src/generated/ by
 * scripts/syncFunctionsGenerated.mjs): the catalog an employer picks from, item validation, the
 * per-item status rule, resolving a linked file on the profile, and the reminder timing rule.
 */
import { EMT_CERTIFICATIONS } from '../constants/certifications';
import { PROFILE_DOCUMENT_CATEGORIES, isCredentialDocumentCategory, getCategoryLabel } from './documentCategories';
import { resolveCertVerification, resolveDocumentVerification } from '../credentials/certificationChecks';
import { mayAutoSendNow } from '../notifications/quietHours';
import { MS_PER_DAY, zonedDateKey } from '../scheduling/time';

export const DOCUMENT_REQUESTS_FLAG = 'document_requests';
export const DOCUMENT_REQUESTS_FLAG_LABEL = 'Request documents from candidates';
export const DOCUMENT_REQUESTS_FLAG_DESCRIPTION =
  'Employers can ask an applicant or team member for specific credentials and documents, with an optional due date. The candidate gets an in-app notice, an email and reminders, and uploads from their requests page.';

export const MAX_REQUEST_ITEMS = 15;
export const MAX_CUSTOM_LABEL = 120;
export const MAX_OPEN_REQUESTS_PER_CANDIDATE = 10;
/** Where the candidate answers a request (in-app notice + email link). */
export const DOCUMENT_REQUESTS_PATH = '/documents/requests';

export type RequestItemKind = 'catalog' | 'custom';
/** What is stored on the item. */
export type StoredItemStatus = 'requested' | 'uploaded';
/** What is shown: stored status plus the live check and the due date. */
export type RequestItemStatus = 'requested' | 'uploaded' | 'reviewed' | 'overdue';

/** A file on the candidate's own profile that answers an item. */
export type DocumentRef =
  | { type: 'certification'; fileUrl: string }
  | { type: 'profileDocument'; documentId: string };

export type StoredRequestItem = {
  id: string;
  kind: RequestItemKind;
  /** Catalog key: `cert:<EMT_CERTIFICATIONS name>` or `doc:<profile document category>`. */
  key?: string | null;
  label: string;
  status: StoredItemStatus;
  documentRef?: DocumentRef | null;
  attachedAt?: string | null;
};

/** Live state of the linked file's automated check; null = this kind of file is never checked. */
export type LinkedCheckState = 'reviewed' | 'rejected' | 'checking' | 'check_unavailable' | 'not_checked' | null;

export const REQUEST_ITEM_STATUS_LABELS: Record<RequestItemStatus, string> = {
  requested: 'Requested',
  uploaded: 'Uploaded',
  reviewed: 'Documents Reviewed',
  overdue: 'Overdue',
};

// ── Catalog ────────────────────────────────────────────────────────────────────────────────────

export type CatalogOption = {
  key: string;
  label: string;
  group: 'credential' | 'document';
  /** How the candidate uploads it: a Certifications-section entry, or a Hireable Profile Document. */
  uploadAs: { type: 'certification'; certName: string } | { type: 'profileDocument'; category: string };
};

/** Credentials (the credential catalog) then the hireable profile document categories ("other" is custom text). */
export function documentRequestCatalog(): CatalogOption[] {
  const creds: CatalogOption[] = EMT_CERTIFICATIONS.map((name) => ({
    key: `cert:${name}`,
    label: name,
    group: 'credential',
    uploadAs: { type: 'certification', certName: name },
  }));
  const docs: CatalogOption[] = PROFILE_DOCUMENT_CATEGORIES.filter((c) => c.key !== 'other').map((c) => ({
    key: `doc:${c.key}`,
    label: c.label,
    group: 'document',
    uploadAs: { type: 'profileDocument', category: c.key },
  }));
  return [...creds, ...docs];
}

/** A custom request that names a medical record (B1): treated exactly like a catalog medical item. */
// 2026-10-13: also plain "physical(s)", "PPD" and "QuantiFERON" (TB skin and blood tests).
const MEDICAL_WORDS = /immuni[sz]|vaccin|\btb\b|tubercul|\bppd\b|quantiferon|hep(atitis)?\s*b|\bphysicals?\b|physical\s*(exam|clearance)|medical|titer|mmr|varicella|flu\s*shot|covid|drug\s*test|dot\s*card|medical\s*card/i;

/** True for a medical item (B1): a medical catalog category, or custom text naming a medical record. */
export function isMedicalRequestItem(item: { kind?: unknown; key?: unknown; label?: unknown }): boolean {
  if (item.kind === 'catalog' && typeof item.key === 'string') return /^doc:(immunizationRecord|medicalClearance)$/.test(item.key);
  return typeof item.label === 'string' && MEDICAL_WORDS.test(item.label);
}

/**
 * A file on an EMT's profile that is a medical record (2026-10-13): a medical category, or a label that
 * names one ("TB test", "DOT medical card", saved under "Other"). Files linked to a medical request item
 * are medical too; that link is checked on the server (functions/src/medicalAccess.ts).
 */
export function isMedicalProfileDocument(d: { category?: unknown; label?: unknown } | null | undefined): boolean {
  if (!d) return false;
  if (typeof d.category === 'string' && /^(immunizationRecord|medicalClearance)$/.test(d.category)) return true;
  return typeof d.label === 'string' && MEDICAL_WORDS.test(d.label);
}

/** A hire credentialing checklist item that asks for a medical record (its label or category names one). */
export function isMedicalChecklistItem(i: { label?: unknown; category?: unknown } | null | undefined): boolean {
  if (!i) return false;
  return isMedicalProfileDocument({ category: i.category, label: i.label }) || (typeof i.category === 'string' && MEDICAL_WORDS.test(i.category));
}

export const MEDICAL_BEFORE_OFFER_MESSAGE =
  'Medical records can be requested only after a job offer: move the applicant to Job Offer or Hired first.';

export function catalogOption(key: unknown): CatalogOption | null {
  if (typeof key !== 'string') return null;
  return documentRequestCatalog().find((o) => o.key === key) ?? null;
}

// ── Create-input validation ──────────────────────────────────────────────────────────────────────

function cleanText(v: unknown, max: number): string {
  if (typeof v !== 'string') return '';
  // eslint-disable-next-line no-control-regex
  return v.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, max);
}

export type ItemInput = { kind?: unknown; key?: unknown; label?: unknown };

/** Validates and normalizes the items an employer picked. Duplicates are dropped. */
export function normalizeRequestItems(raw: unknown): { items: StoredRequestItem[]; error: string | null } {
  if (!Array.isArray(raw) || raw.length === 0) return { items: [], error: 'Pick at least one document to request.' };
  if (raw.length > MAX_REQUEST_ITEMS) return { items: [], error: `Ask for up to ${MAX_REQUEST_ITEMS} documents at a time.` };
  const items: StoredRequestItem[] = [];
  const seen = new Set<string>();
  for (const r of raw as ItemInput[]) {
    if (!r || typeof r !== 'object') return { items: [], error: 'One of the documents could not be read.' };
    if (r.kind === 'catalog') {
      const opt = catalogOption(r.key);
      if (!opt) return { items: [], error: 'One of the documents is not on the list.' };
      if (seen.has(opt.key)) continue;
      seen.add(opt.key);
      items.push({ id: `i${items.length + 1}`, kind: 'catalog', key: opt.key, label: opt.label, status: 'requested', documentRef: null });
    } else if (r.kind === 'custom') {
      const label = cleanText(r.label, MAX_CUSTOM_LABEL);
      if (label.length < 2) return { items: [], error: 'Describe the document you need (at least 2 characters).' };
      const k = `custom:${label.toLowerCase()}`;
      if (seen.has(k)) continue;
      seen.add(k);
      items.push({ id: `i${items.length + 1}`, kind: 'custom', key: null, label, status: 'requested', documentRef: null });
    } else {
      return { items: [], error: 'One of the documents could not be read.' };
    }
  }
  if (items.length === 0) return { items: [], error: 'Pick at least one document to request.' };
  return { items, error: null };
}

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isYmd(v: unknown): v is string {
  if (typeof v !== 'string') return false;
  const m = YMD.exec(v);
  if (!m) return false;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() + 1 === Number(m[2]) && d.getUTCDate() === Number(m[3]);
}

/** Calendar-day arithmetic on YYYY-MM-DD (zone free). */
export function addDaysYmd(ymd: string, days: number): string {
  const m = YMD.exec(ymd);
  if (!m) return ymd;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days));
  return d.toISOString().slice(0, 10);
}

/** A due date must be a real day, not before today, and at most a year out. null = fine. */
export function dueDateError(dueDate: unknown, todayYmd: string): string | null {
  if (dueDate == null || dueDate === '') return null;
  if (!isYmd(dueDate)) return 'Pick a valid due date.';
  if (dueDate < todayYmd) return 'The due date is in the past.';
  if (dueDate > addDaysYmd(todayYmd, 366)) return 'Pick a due date within the next year.';
  return null;
}

/** "Oct 12, 2026" for a YYYY-MM-DD (no time zone shift). */
export function formatDueDate(ymd: string): string {
  if (!isYmd(ymd)) return ymd;
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

// ── Status ───────────────────────────────────────────────────────────────────────────────────────

/** Check status (certificationChecks / aiVerification) to the state shown. */
export function checkStateFromVerification(status: unknown): Exclude<LinkedCheckState, null> {
  switch (status) {
    case 'under_review':
      return 'reviewed';
    case 'pending':
      return 'checking';
    case 'rejected':
    case 'failed':
      return 'rejected';
    case 'unavailable':
      return 'check_unavailable';
    default:
      return 'not_checked';
  }
}

/**
 * The status shown for one item.
 *  - reviewed: the linked file passed the automated check (Documents Reviewed).
 *  - A linked file of a kind that is never checked automatically (background check, drug screen,
 *    a resume) is complete once uploaded.
 *  - overdue: past the due date (the day after it, in the candidate's zone) and not complete.
 *  - uploaded: a file is linked but not (yet) reviewed.
 *  - requested: nothing linked, or the linked file is no longer on the profile.
 */
export function itemDisplayStatus(input: {
  linked: boolean;
  checkState: LinkedCheckState;
  dueDate?: string | null;
  todayYmd: string;
}): { status: RequestItemStatus; complete: boolean } {
  const reviewed = input.linked && input.checkState === 'reviewed';
  const complete = reviewed || (input.linked && input.checkState === null);
  if (reviewed) return { status: 'reviewed', complete: true };
  if (!complete && input.dueDate && isYmd(input.dueDate) && input.todayYmd > input.dueDate) return { status: 'overdue', complete: false };
  if (input.linked) return { status: 'uploaded', complete };
  return { status: 'requested', complete: false };
}

type ProfileLike = {
  professional?: { certifications?: unknown } | null;
  profileDocuments?: unknown;
  certificationChecks?: unknown;
};

export type LinkedDocument = {
  found: boolean;
  checkState: LinkedCheckState;
  /** documentId for getCandidateDocumentUrl ('cert:<index>' or a profileDocuments id). */
  viewDocumentId: string | null;
  /** Certification name or document label, for the list. */
  label: string | null;
};

/** Finds the linked file on the candidate's live profile and its live check state. */
export function resolveLinkedDocument(profile: ProfileLike | null | undefined, ref: DocumentRef | null | undefined): LinkedDocument {
  const none: LinkedDocument = { found: false, checkState: null, viewDocumentId: null, label: null };
  if (!profile || !ref || typeof ref !== 'object') return none;
  if (ref.type === 'certification') {
    if (typeof ref.fileUrl !== 'string' || !ref.fileUrl) return none;
    const certs = Array.isArray(profile.professional?.certifications) ? (profile.professional!.certifications as unknown[]) : [];
    const idx = certs.findIndex((c) => !!c && typeof c === 'object' && (c as { fileUrl?: unknown }).fileUrl === ref.fileUrl);
    if (idx < 0) return none;
    const cert = certs[idx] as { name?: unknown };
    const v = resolveCertVerification(cert, profile.certificationChecks);
    return {
      found: true,
      checkState: checkStateFromVerification(v?.status),
      viewDocumentId: `cert:${idx}`,
      label: typeof cert.name === 'string' ? cert.name.trim() : 'Certification',
    };
  }
  if (ref.type === 'profileDocument') {
    if (typeof ref.documentId !== 'string' || !ref.documentId) return none;
    const docs = Array.isArray(profile.profileDocuments) ? (profile.profileDocuments as Array<Record<string, unknown>>) : [];
    const d = docs.find((x) => x && x.id === ref.documentId && typeof x.downloadUrl === 'string' && x.downloadUrl);
    if (!d) return none;
    const category = typeof d.category === 'string' ? d.category : 'other';
    const checked = isCredentialDocumentCategory(category);
    return {
      found: true,
      checkState: checked ? checkStateFromVerification(resolveDocumentVerification(d, profile.certificationChecks)?.status) : null,
      viewDocumentId: ref.documentId,
      label: (typeof d.label === 'string' && d.label.trim()) || getCategoryLabel(category),
    };
  }
  return none;
}

export type ComputedItem = {
  id: string;
  kind: RequestItemKind;
  key: string | null;
  label: string;
  status: RequestItemStatus;
  complete: boolean;
  checkState: LinkedCheckState;
  linked: boolean;
  viewDocumentId: string | null;
  linkedLabel: string | null;
};

export function computeRequestItems(items: StoredRequestItem[], profile: ProfileLike | null | undefined, dueDate: string | null | undefined, todayYmd: string): ComputedItem[] {
  return (Array.isArray(items) ? items : []).map((it) => {
    const linked = it.documentRef ? resolveLinkedDocument(profile, it.documentRef) : null;
    const isLinked = !!linked?.found;
    const s = itemDisplayStatus({ linked: isLinked, checkState: isLinked ? linked!.checkState : null, dueDate, todayYmd });
    return {
      id: it.id,
      kind: it.kind,
      key: it.key ?? null,
      label: it.label,
      status: s.status,
      complete: s.complete,
      checkState: isLinked ? linked!.checkState : null,
      linked: isLinked,
      viewDocumentId: isLinked ? linked!.viewDocumentId : null,
      linkedLabel: isLinked ? linked!.label : null,
    };
  });
}

/**
 * Items the candidate still has to act on: nothing linked yet, or the linked file failed the check
 * (it needs a new upload). An uploaded file still being checked is not chased.
 */
export function outstandingItems(items: ComputedItem[]): ComputedItem[] {
  return items.filter((i) => !i.complete && (!i.linked || i.checkState === 'rejected'));
}

// ── Reminders ────────────────────────────────────────────────────────────────────────────────────

export type ReminderKind = 'initial' | 'two_days' | 'day_before' | 'due_day';

export type ReminderState = {
  /** The first email (sent at request time, or at the next allowed hour if that was quiet). */
  initialSent?: boolean;
  /** Recipient-local day of the last email, YYYY-MM-DD: at most one per request per day. */
  lastSentDay?: string | null;
  two_days?: boolean;
  day_before?: boolean;
  due_day?: boolean;
  count?: number;
};

export const REMINDER_AFTER_MS = 2 * MS_PER_DAY;

/**
 * Which email (if any) an open request should send right now, in the candidate's zone `tz`.
 *  - Nothing when the request is not open or every item is answered (uploaded or reviewed).
 *  - At most one per request per local day.
 *  - Email quiet hours (1-6 AM local): nothing now; the hourly job tries again.
 *  - The first email if it has not gone yet; then on the due date, the day before it, and a nudge
 *    two days after the request (only while the due date has not passed). Each one once.
 */
export function documentRequestReminderDue(input: {
  open: boolean;
  outstanding: number;
  createdAtMs: number;
  dueDate?: string | null;
  reminders?: ReminderState | null;
  nowMs: number;
  tz: string;
}): { kind: ReminderKind | null; reason: string } {
  const r = input.reminders || {};
  if (!input.open) return { kind: null, reason: 'not_open' };
  if (input.outstanding <= 0) return { kind: null, reason: 'nothing_outstanding' };
  const today = zonedDateKey(input.nowMs, input.tz);
  if (r.lastSentDay === today) return { kind: null, reason: 'already_today' };
  if (!mayAutoSendNow(input.tz, 'email', input.nowMs)) return { kind: null, reason: 'quiet_hours' };
  if (!r.initialSent) return { kind: 'initial', reason: 'first_email' };
  const due = input.dueDate && isYmd(input.dueDate) ? input.dueDate : null;
  if (due && today === due && !r.due_day) return { kind: 'due_day', reason: 'due_today' };
  if (due && today === addDaysYmd(due, -1) && !r.day_before) return { kind: 'day_before', reason: 'due_tomorrow' };
  const pastDue = due ? today > due : false;
  if (!pastDue && !r.two_days && input.nowMs >= input.createdAtMs + REMINDER_AFTER_MS) return { kind: 'two_days', reason: 'two_days_after' };
  return { kind: null, reason: 'nothing_due' };
}

/** Subject and plain-text lines of the candidate's email (the server adds HTML escaping). */
export function documentRequestEmailContent(input: {
  kind: ReminderKind;
  orgName: string;
  firstName?: string | null;
  itemLabels: string[];
  dueDate?: string | null;
}): { subject: string; intro: string; items: string[]; dueLine: string | null; ctaLabel: string } {
  const org = input.orgName || 'An employer';
  const due = input.dueDate && isYmd(input.dueDate) ? formatDueDate(input.dueDate) : null;
  const hi = input.firstName ? `Hi ${input.firstName},` : 'Hi,';
  const subject =
    input.kind === 'initial'
      ? `${org} asked you for documents`
      : input.kind === 'due_day'
        ? `Reminder: documents for ${org} are due today`
        : input.kind === 'day_before'
          ? `Reminder: documents for ${org} are due tomorrow`
          : `Reminder: ${org} is waiting for your documents`;
  const intro =
    input.kind === 'initial'
      ? `${hi} ${org} asked you to upload these documents on Arevina:`
      : `${hi} ${org} is still waiting for these documents:`;
  return {
    subject,
    intro,
    items: input.itemLabels.slice(0, MAX_REQUEST_ITEMS),
    dueLine: due ? `Please upload them by ${due}.` : null,
    ctaLabel: 'Upload documents',
  };
}
